<template>
  <div class="order--aditional-fee-form">
    <Form ref="orderAdditionalFeeForm" :model="formData" :disabled="isPublished">
      <FormItem :label="$t('order.additional_fee_description')" prop="description" :rules="{ required: true, message: $t('order.additional_fee_description_is_required') }">
        <Input v-model="formData.description"></Input>
      </FormItem>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.order_number')" prop="order_number" required>
            <AutoComplete v-model="formData.order_number" disabled></AutoComplete>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.invoice_number')" prop="invoice_number">
            <Input v-model="formData.invoice_number"></Input>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.order_change_reasoning')" prop="change_reasoning" :rules="{ required: true, message: $t('order.order_change_reasoning_is_required') }">
            <Select v-model="formData.change_reasoning">
              <OptionGroup :label="$t('order.order_item_change')">
                <Option value="product_qty_change">{{ $t('order.order_item_product_qty_change') }}</Option>
              </OptionGroup>
              <OptionGroup :label="$t('order.engineering_craft')">
                <Option value="bom_changed">{{ $t('order.bom_changed') }}</Option>
                <Option value="design_changed">{{ $t('order.design_changed') }}</Option>
              </OptionGroup>
              <OptionGroup :label="$t('order.order_change_others')">
                <Option value="ship">{{ $t('order.shipping') }}</Option>
                <Option value="tax">{{ $t('order.tax') }}</Option>
                <Option value="exchange_rate_changed">{{ $t('order.exchange_rate_changed') }}</Option>
                <Option value="over_charged">{{ $t('order.over_changed') }}</Option>
                <Option value="delivery_date_changed">{{ $t('order.delivery_date_changed') }}</Option>
                <Option value="service_agreement_changed">{{ $t('order.service_agreement_changed') }}</Option>
                <Option value="others">{{ $t('order.change_reason_others') }}</Option>
              </OptionGroup>
            </Select>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('currency')" prop="currency" :rules="{ required: true, message: $t('order.currency_is_required') }">
            <CurrencySelect v-model="formData.currency" disabled></CurrencySelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('exchange_rate')" prop="exchange_rate" :rules="{ required: true, message: $t('order.exchange_rate_is_required') }">
            <InputNumber v-model="formData.exchange_rate"></InputNumber>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" v-if="formData.order_change_reasoning === 'others'">
        <Col span="24">
          <FormItem :label="$t('order.order_change_reasoning_message')" prop="change_reason_message" :rules="{ required: true, message: $t('order.change_reasonning_message_is_required') }">
            <Input v-model="formData.change_reason_message"></Input>
          </FormItem>
        </Col>
      </Row>

      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.order_change_date')"
                    prop="date"
                    :rules="{ required: true, message: $t('order.order_change_date_is_required') }">
            <DatePicker v-model="formData.date" type="date"></DatePicker>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.order_change_sub_order_number')">
            <Input v-model="formData.sub_order_number"></Input>
            <div style="color: #808695; font-size: 12px; text-align: right;">{{ $t('order.order_change_sub_order_number_desc') }}</div>
          </FormItem>
        </Col>
      </Row>

      <Alert type="warning" show-icon>
        {{ $t('order.order_change_warning') }}
      </Alert>

      <Spin v-if="loadingItems"></Spin>
      <div v-else>
        <div class="block" style="padding: 20px 0;">
          <h3>{{ $t('order.order_item_changes') }}</h3>
          <p class="info margin-bottom-20">{{ $t('order.order_additional_fee_order_item_change_message') }}</p>
          <div v-for="(item, index) in productItems" :key="index">
            <OrderAdditionalFeeProductItemForm
              :class="index % 2 ? 'even' : 'odd'"
              :data="item"
              :index="item.rowNo"
              :disabled="isPublished"
              @on-deleted="handleItemDeleted">
            </OrderAdditionalFeeProductItemForm>
          </div>
          <FormItem>
            <Button type="dashed" icon="md-add" @click="handleAddItem('manufacturer_part')">{{ $t('order.additional_fee_add_item') }}</Button>
          </FormItem>
        </div>

        <div class="block" style="padding: 20px 0;">
          <h3>{{ $t('order.additional_order_change_items') }}</h3>
          <p class="info margin-bottom-20">{{ $t('order.order_additional_fee_item_change_message') }}</p>

          <div v-for="(item, index) in feeItems" :key="index">
            <OrderAdditionalFeeItemForm
              :class="index % 2 ? 'even' : 'odd'"
              :data="item"
              :index="item.rowNo"
              :disabled="isPublished"
              @on-deleted="handleItemDeleted">
            </OrderAdditionalFeeItemForm>
          </div>
          <FormItem>
            <Button type="dashed" icon="md-add" @click="handleAddItem('fee_type')">{{ $t('order.additional_fee_add_item') }}</Button>
          </FormItem>
        </div>

        <div style="font-weight: bold; font-size: 200%; text-align: right; margin: 20px;">
          {{ $t('order.additional_fee_total') }}:
          {{ intlCurrencyNumber(formData.currency, itemsTotalAmount, 2, 2) }}
        </div>

        <FormItem :label="$t('order.additional_fee_status')" style="width: 300px">
          <Select v-model="formData.status">
            <Option value="open">{{ $t('order.change_status_open') }}</Option>
            <Option value="approved">{{ $t('order.change_status_approved') }}</Option>
            <Option value="invalided">{{ $t('order.change_status_invalided') }}</Option>
          </Select>
          <div v-if="formData.status === 'approved'" class="margin-top-20">
            <Checkbox v-model="formData.sync2erp">{{ $t('order.additional_fee_sync2erp') }}</Checkbox>
            <Checkbox v-model="formData.sync2qbo">{{ $t('order.additional_fee_sync2qbo') }}</Checkbox>
          </div>
        </FormItem>

        <FormItem class="text-align-right">
          <router-link v-if="formData.quote"
                       target="_blank"
                       :to="{ name: 'menu.quote.edit', params: { quoteId: formData.quote.id } }">
                       {{ $t('order.check_associated_quote') }}
          </router-link>
          <Button v-else-if="!!formData.id" @click="handleCreateQuote" type="default" :loading="creatingQuote">{{ $t('order.additional_fee_create_quote') }}</Button>&nbsp;
          <Button type="primary" @click="handleSave" :loading="saving" :disabled="!itemsValid">{{ $t('save') }}</Button>
          <div class="success" v-if="success">{{ $t('save_success_message') }}</div>
        </FormItem>
      </div>
    </Form>
  </div>
</template>

<script>
import CurrencySelect from '@/components/currency'
import OrderAdditionalFeeItemForm from './order-additional-fee-item-form'
import OrderAdditionalFeeProductItemForm from './order-additional-fee-product-item-form'
import { intlCurrencyNumber } from '@/libs/util'
import { mapActions } from 'vuex'
import moment from 'moment'
export default {
  name: "OrderAdditionalFeeForm",
  components: {
    CurrencySelect,
    OrderAdditionalFeeItemForm,
    OrderAdditionalFeeProductItemForm
  },
  props: {
    recordData: {
      type: Object,
      required: false
    },
    isNew: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      saving: false,
      success: false,
      feeTypeItemProperties: {
        referenced_type: 'fee_type',
        referenced_id: null,
        description: '',
        quantity: 1,
        unit_cost: 0,
        unit_price: 0
      },
      productTypeItemProperties: {
        referenced_type: 'manufacturer_part',
        referenced_id: null,
        quantity: 1,
        unit_price: 0
      },
      formData: {
        status: 'open',
        items: []
      },
      loadingItems: false,
      creatingQuote: false
    }
  },
  computed: {
    itemsTotalAmount () {
      return this.formData ? _.sumBy(this.formData.items, x => x.quantity * x.unit_price) : 0
    },
    isPublished () {
      return this.recordData.status === 'published'
    },
    feeItems () {
      return this.formData ? this.formData.items.filter((x) => x.referenced_type === 'fee_type') : []
    },
    productItems () {
      return this.formData ? this.formData.items.filter((x) => x.referenced_type === 'manufacturer_part') : []
    },
    itemsValid () {
      return this.formData.items.every((item) => {
        return item.referenced_id
      })
    }
  },
  mounted () {
    this.formDataInit()
  },
  methods: {
    ...mapActions([
      'createOrderChangeRecord',
      'updateOrderChangeRecord',
      'getOrderChangeRecordItems',
      'createOrderChangeRecordAsQuote'
    ]),
    intlCurrencyNumber,
    handleSave () {
      this.$refs.orderAdditionalFeeForm.validate((valid) => {
        if (valid) {
          this.saving = true
          this.success = false
          this.formData['date'] = moment(this.formData.date).format('YYYY-MM-DD')
          if (this.formData.id) {
            this.updateOrderChangeRecord({ recordId: this.formData.id, data: this.formData })
              .then((resp) => {
                this.formData = resp.data
                this.success = true
              })
              .finally(() => {
                this.saving = false
              })
          } else {
            this.createOrderChangeRecord({ orderId: this.formData.order_id, data: this.formData })
              .then((resp) => {
                this.formData = resp.data
                this.success = true
              })
              .finally(() => {
                this.saving = false
              })
          }
        }
      })
    },
    handleAddItem (type) {
      let item = type === 'fee_type' ? _.clone(this.feeTypeItemProperties) : _.clone(this.productTypeItemProperties)

      // When adding new item without got saved, we may not be able to identify the position of it while deleting the item,
      // so we loop over the items and assigning a rowNo for tracking the position in the items list
      item.rowNo = this.formData.items.length
      this.formData.items.push(item)
    },
    formDataInit () {
      this.formData = Object.assign({}, this.formData, this.recordData)
    },
    handleItemDeleted ({ itemId, index, referencedType }) {
      const indexDel = this.formData.items.findIndex((item) => {
        if (itemId) {
          return item.id === itemId && item.referenced_type === referencedType
        }
        return item.rowNo !== undefined && item.rowNo === index
      })
      if (indexDel >= 0) {
        this.formData.items.splice(indexDel, 1)
      }
    },
    handleCreateQuote () {
      if (this.formData.id) {
        this.creatingQuote = true
        this.createOrderChangeRecordAsQuote(this.formData.id)
          .then((resp) => {
            this.formData.quote = resp.data
          })
          .finally(() => {
            this.creatingQuote = false
          })
      }
    }
  },
  watch: {
    recordData: {
      deep: true,
      handler () {
        this.formDataInit()

        if (this.recordData.id) {
          this.loadingItems = true
          this.getOrderChangeRecordItems(this.recordData.id)
            .then((resp) => {
              this.formData.items = resp.data
              this.formData.items.forEach(function (item, index) {
                item.rowNo = index
              })
            })
            .finally(() => {
              this.loadingItems = false
            })
        }
      }
    },
    isNew (val) {
      if (val) {
        // Clear other fields
        this.formData = { items: [] }

        this.$refs['orderAdditionalFeeForm'].resetFields()
        this.formDataInit()
      }
    }
  }
}
</script>
